import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, ListTodo, Building2, CalendarDays, TrendingUp,
  Handshake, Wallet, Users, FileText, Calendar, Search, LogOut, Menu,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/tasks", label: "My Tasks", icon: ListTodo, testId: "nav-tasks" },
  { to: "/venues", label: "Venues", icon: Building2, testId: "nav-venues" },
  { to: "/events", label: "Events", icon: CalendarDays, testId: "nav-events" },
  { to: "/sales", label: "Sales / Partnerships", icon: TrendingUp, testId: "nav-sales" },
  { to: "/sponsorships", label: "Sponsorships", icon: Handshake, testId: "nav-sponsorships" },
  { to: "/finance", label: "Finance", icon: Wallet, testId: "nav-finance" },
  { to: "/team", label: "Team", icon: Users, testId: "nav-team" },
  { to: "/documents", label: "Documents", icon: FileText, testId: "nav-documents" },
  { to: "/calendar", label: "Calendar", icon: Calendar, testId: "nav-calendar" },
];

const NavItems = ({ onClick }) => (
  <nav className="flex flex-col gap-1 p-3">
    {NAV.map((n) => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.to === "/"}
        onClick={onClick}
        data-testid={n.testId}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
            isActive
              ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 pl-2"
              : "text-slate-700 hover:bg-slate-100"
          }`
        }
      >
        <n.icon className="h-4 w-4" strokeWidth={1.75} />
        <span>{n.label}</span>
      </NavLink>
    ))}
  </nav>
);

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const nav = useNavigate();

  const doSearch = async (val) => {
    setQ(val);
    if (!val.trim()) { setResults(null); return; }
    try {
      const { data } = await api.get("/search", { params: { q: val } });
      setResults(data);
    } catch { setResults(null); }
  };

  const go = (path) => { setQ(""); setResults(null); nav(path); };

  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => doSearch(e.target.value)}
        placeholder="Search venues, events, tasks, leads, sponsors..."
        className="pl-9 bg-slate-50 border-slate-200"
      />
      {results && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-auto">
          {["venues","events","tasks","leads","sponsors"].map((k) => (
            results[k]?.length > 0 && (
              <div key={k} className="p-2">
                <div className="text-xs uppercase tracking-widest text-slate-500 px-2 py-1">{k}</div>
                {results[k].map((r) => (
                  <button
                    key={r.id}
                    data-testid={`search-result-${k}-${r.id}`}
                    onClick={() => go(
                      k === "venues" ? `/venues/${r.id}` :
                      k === "events" ? `/events/${r.id}` :
                      k === "tasks" ? `/tasks/all` :
                      k === "leads" ? `/sales` : `/sponsorships`
                    )}
                    className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm"
                  >
                    {r.name || r.title || r.company || r.brand}
                  </button>
                ))}
              </div>
            )
          ))}
          {Object.values(results).every((arr) => !arr?.length) && (
            <div className="p-4 text-sm text-slate-500">No matches for "{q}"</div>
          )}
        </div>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="p-5 border-b border-slate-200">
          <div className="text-lg font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            GameLink<span className="text-blue-600">OS</span>
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Internal Operations</div>
        </div>
        <div className="flex-1 overflow-y-auto"><NavItems /></div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 h-16 flex items-center gap-4 px-4 md:px-8 sticky top-0 z-40">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <div className="p-5 border-b border-slate-200">
                <div className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  GameLink<span className="text-blue-600">OS</span>
                </div>
              </div>
              <NavItems onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex-1 flex justify-center lg:justify-start">
            <GlobalSearch />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2 hover:bg-slate-100 rounded-md p-1.5 transition-colors">
                <Avatar className="h-8 w-8 bg-blue-600">
                  <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{(user?.role || "").replace("_", " ")}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-600">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-8 max-w-[1600px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
